import { HrefLink as Link } from "@/components/ui/href-link";
import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";

import { AuthField, CONTROL } from "@/components/dashboard/auth-field";
import { app, auth } from "@/content";
import { requestLogin } from "@/config";
import { AUTH_NOTICE_KEY } from "@/constants/events";
import { useSessionState } from "@/context/UserContext";

/**
 * Sign-in.
 *
 * ## What happens to the credential
 *
 * Nothing, here. The API answers this POST with a `Set-Cookie` — `HttpOnly`,
 * `Secure`, `SameSite=Lax`, scoped to the parent domain so `admin.` and `api.`
 * share it — and the browser stores it where script cannot reach. The response
 * body also carries a token, for the mobile app; this form never reads it.
 *
 * That is the difference from the prototype, which put its JWT in `localStorage`.
 * That single decision is why its "sign out" was a `localStorage.clear()` that
 * left a thirty-day token valid on the server, and why an XSS anywhere on the
 * page could lift a working admin credential.
 *
 * After the cookie is set the session is re-read rather than assembled from the
 * login response, so the app's idea of who is signed in comes from the same
 * `GET /auth/session` every reload uses. One shape, one source.
 */
export const Route = createFileRoute("/_auth/_anonymous/login")({
  component: LoginPage,
});

function LoginPage() {
  const { refresh } = useSessionState();
  const passwordReset =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("passwordReset") === "1";
  const resubmitted =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("resubmitted") === "1";
  const sessionSuperseded =
    typeof window !== "undefined" &&
    (() => {
      try {
        if (sessionStorage.getItem(AUTH_NOTICE_KEY) !== "session_superseded") return false;
        sessionStorage.removeItem(AUTH_NOTICE_KEY);
        return true;
      } catch {
        return false;
      }
    })();

  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPending(true);
    setError(null);

    const form = new FormData(event.currentTarget);
    const result = await requestLogin(
      String(form.get("identifier") ?? "").trim(),
      String(form.get("password") ?? ""),
    );

    if (!result.ok) {
      setError(result.error);
      setPending(false);
      return;
    }

    /* The cookie exists now; this is what turns it into a session in the app —
       and that is the whole job. `RequireAnonymous` above this route sees the
       session flip and redirects, honouring the destination the session guard
       remembered. Navigating from here as well would race it, and the guard would
       win, which is how the remembered screen got lost. */
    await refresh();
  }

  return (
    <form
      onSubmit={onSubmit}
      className="flex flex-col gap-5 rounded-xl border border-white/10 bg-navy-800 p-7 shadow-navy"
      noValidate
    >
      {/* The console's name, not the page's — the heading already says that, and
          repeating it made the kicker pure noise. */}
      <div>
        <span className="bracket">{app.name}</span>
        <h1 className="mt-5 text-lg font-bold leading-head text-on-navy">{auth.signInTitle}</h1>
        <p className="mt-2 text-sm leading-body text-on-navy-dim">{auth.signInLead}</p>
      </div>

      {sessionSuperseded ? (
        <p
          role="status"
          className="rounded-lg border border-orange/40 bg-orange/10 px-3 py-3 text-xs font-semibold leading-body text-on-navy-2"
        >
          {auth.sessionSupersededBanner}
        </p>
      ) : null}

      {passwordReset ? (
        <p
          role="status"
          className="rounded-lg border border-orange/40 bg-orange/10 px-3 py-3 text-xs font-semibold leading-body text-on-navy-2"
        >
          {auth.passwordResetDone}
        </p>
      ) : null}

      {resubmitted ? (
        <p
          role="status"
          className="rounded-lg border border-orange/40 bg-orange/10 px-3 py-3 text-xs font-semibold leading-body text-on-navy-2"
        >
          {auth.resubmittedBanner}
        </p>
      ) : null}

      <AuthField id="identifier" label={auth.identifier} required>
        <input
          id="identifier"
          name="identifier"
          className={CONTROL}
          /* Not `type="email"`: the field takes a phone number too, and
             `type="email"` makes a mobile browser refuse one before it is sent. */
          type="text"
          inputMode="email"
          autoComplete="username"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          dir="ltr"
          required
          autoFocus
          aria-invalid={error ? true : undefined}
        />
      </AuthField>

      <AuthField id="password" label={auth.password} required>
        <input
          id="password"
          name="password"
          type="password"
          className={CONTROL}
          autoComplete="current-password"
          required
          aria-invalid={error ? true : undefined}
        />
      </AuthField>

      {error ? (
        <p
          role="alert"
          className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2.5 text-xs font-semibold text-red-300"
        >
          {error}
        </p>
      ) : null}

      <button
        type="submit"
        disabled={pending}
        className="btn-primary h-11 w-full rounded-lg text-sm font-bold transition-transform duration-300 ease-brand disabled:pointer-events-none disabled:opacity-60"
      >
        {pending ? auth.signingIn : auth.signIn}
      </button>

      <Link
        to="/forgot-password"
        className="self-start text-xs font-semibold text-orange transition-opacity duration-200 ease-brand hover:opacity-80"
      >
        {auth.forgotPassword}
      </Link>
    </form>
  );
}
